import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { canAccessSecrets, roleSettled } from '../lib/roles';
import { useMe } from '../lib/session';
import { EmptyState } from './EmptyState';
import { RepoSecretsTab } from './RepoSecretsTab';

// Owner gate over the repo workspace's Secrets tab (#1418). All three
// `/v1/repos/:owner/:repo/secrets` routes are owner-only now — the READ included,
// because a repo's secret NAMES (`STRIPE_LIVE_KEY` in `prod`) are the
// disclosure — so a viewer/member/admin sees no list and no controls.
//
// The gate wraps rather than hides-within: `<RepoSecretsTab/>` calls
// `useRepoSecrets` on mount, so keeping it UNMOUNTED is what stops the SPA
// firing a `GET` that can only ever 403. `repoTabs` already drops the tab from
// the bar; this is the deep-link half — a bookmark must answer with the reason,
// never an error box.
//
// Shut until `/v1/me` has SETTLED for this tenant (`roleSettled` — not
// `!isLoading`, which lets through both the org-switch placeholder carrying the
// previous org's role and a failed request), and `denied` is not `!allowed`:
// unsettled means unknown, not "no", so neither branch paints.

export type RepoSecretsGateProps = {
  readonly fullName: string;
  readonly token: string | null;
};

export function RepoSecretsGate(props: RepoSecretsGateProps): JSX.Element {
  const me = useMe(props.token);
  const allowed = (): boolean => roleSettled(me) && canAccessSecrets(me.data?.role);
  const denied = (): boolean => roleSettled(me) && !canAccessSecrets(me.data?.role);

  return (
    <Show
      when={allowed()}
      fallback={
        <Show when={denied()}>
          <EmptyState message={t('repo_secrets.owner_only')} />
        </Show>
      }
    >
      <RepoSecretsTab fullName={props.fullName} token={props.token} />
    </Show>
  );
}
